import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FaBan, FaPlay, FaSearch } from 'react-icons/fa'
import { Confirm } from 'notiflix/build/notiflix-confirm-aio'
import { Notify } from 'notiflix/build/notiflix-notify-aio'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api from '../../../../helpers/apiRequester'
import { colors } from '../../../../ui'
import styles from '../shared/mhlShared.module.scss'
import localStyles from './MilitaryHearingLossWebsiteVisitors.module.scss'
import { Pill, StatTile } from '../shared/SourceTabs'
import type { ChaseCampaignResponse, ChaseEnrolment, ChaseStatus } from '../shared/mhlTypes'
import { formatDate, formatDateTime } from '../shared/mhlFormatting'
import { MHL_PAGE_SIZE, TablePagination, usePagedRows } from '../shared/TablePagination'
import { ChaseMessagesTable } from './ChaseMessagesTable'
import { JOURNEY_LABELS, journeyTone } from './WebformSignupsTable'

interface Props {
    data: ChaseCampaignResponse | null
    loading: boolean
    rangeLabel: string
    /** Called after a chase is stopped / resumed so the parent can reload. */
    onChanged?: () => void
}

type EnrolFilter = 'active' | 'due' | 'stopped' | 'capped' | 'awaiting_link' | 'complete' | 'all'

const todayIso = () => new Date().toISOString().split('T')[0]

const CHASE_LABELS: Record<ChaseStatus, string> = {
    ...JOURNEY_LABELS,
    awaiting_link: 'E-sign link not sent',
    rejected: 'Rejected at review',
    stopped: 'Chase stopped',
}

const chaseTone = (status: ChaseStatus) =>
    status === 'awaiting_link' || status === 'rejected' ? 'muted' : status === 'stopped' ? 'bad' : journeyTone(status)

const matchesFilter = (e: ChaseEnrolment, filter: EnrolFilter) => {
    switch (filter) {
        case 'active':
            return e.active
        case 'due':
            return e.active && !!e.next_chase_due && e.next_chase_due <= todayIso()
        case 'stopped':
            return e.status === 'stopped'
        case 'capped':
            return e.capped && (e.status === 'not_signed' || e.status === 'signed_no_docs')
        case 'awaiting_link':
            return e.status === 'awaiting_link' || e.status === 'rejected'
        case 'complete':
            return e.status === 'complete'
        default:
            return true
    }
}

/**
 * Drop-off chase campaign: who is being chased (lead exists but no signature,
 * or signed without ID documents), how far through the 5-message sequence they
 * are, and every email/SMS that has gone out.
 */
export const ChaseCampaignPanel = ({ data, loading, rangeLabel, onChanged }: Props) => {
    const navigate = useNavigate()
    const [filter, setFilter] = useState<EnrolFilter>('active')
    const [search, setSearch] = useState('')
    const [updatingRef, setUpdatingRef] = useState<string | null>(null)

    // Stop (opt-out) or resume the chase for one client, straight from the table.
    const setChaseStopped = (e: ChaseEnrolment, stop: boolean) => {
        const name = `${e.firstname} ${e.lastname}`.trim()
        const run = async (reason?: string) => {
            setUpdatingRef(e.case_ref)
            try {
                const res = await api({ url: '/military-hearing-loss-chase-stop', data: { case_ref: e.case_ref, stop, reason } })
                if (res.data?.success) {
                    Notify.success(stop ? `Chase stopped for ${name}` : `Chase resumed for ${name}`)
                    onChanged?.()
                } else {
                    Report.failure('Update failed', res.data?.message || 'Could not update the chase campaign', 'Okay')
                }
            } catch (err: any) {
                Report.failure('Update failed', err?.response?.data?.message || err?.message || 'Could not update the chase campaign', 'Okay')
            } finally {
                setUpdatingRef(null)
            }
        }
        if (stop) {
            Confirm.prompt(
                `Stop chase for ${name}?`,
                'No further chase emails or SMS will be sent. Reason (optional):',
                'Client opted out',
                'Stop chase',
                'Cancel',
                (answer: string) => void run(answer),
                () => {},
                { okButtonBackground: colors.bad, titleColor: colors.ink },
            )
        } else {
            Confirm.show(
                `Resume chase for ${name}?`,
                'The chase sequence picks up where it left off and the client may receive further messages.',
                'Resume',
                'Cancel',
                () => void run(),
                () => {},
                { okButtonBackground: colors.accent, titleColor: colors.ink },
            )
        }
    }

    const enrolled = useMemo(() => {
        const term = search.trim().toLowerCase()
        return (data?.enrolled || []).filter((e) => {
            if (!matchesFilter(e, filter)) return false
            if (!term) return true
            return (
                `${e.firstname} ${e.lastname}`.toLowerCase().includes(term) ||
                (e.email || '').toLowerCase().includes(term) ||
                (e.telephone || '').toLowerCase().includes(term) ||
                e.case_ref.toLowerCase().includes(term)
            )
        })
    }, [data, filter, search])

    const { rows, page, totalPages, setPage, pageSize } = usePagedRows(enrolled, MHL_PAGE_SIZE)

    const clientNames = useMemo(() => {
        const map: Record<string, string> = {}
        for (const e of data?.enrolled || []) map[e.case_ref] = `${e.firstname} ${e.lastname}`.trim()
        return map
    }, [data])

    const summary = data?.summary

    return (
        <>
            {summary && (
                <div className={`${localStyles.statsRowFive} ${styles.statStrip}`}>
                    <StatTile label="Currently enrolled" value={summary.enrolled} accent note="Dropped off and still being chased" />
                    <StatTile label="Not signed" value={summary.notSigned} tone="bad" note="Client data saved, CFA not signed" />
                    <StatTile label="Signed · no ID docs" value={summary.signedNoDocs} tone="warn" note="Signed but identity uploads missing" />
                    <StatTile
                        label={`Messages sent (${rangeLabel})`}
                        value={summary.messagesInRange}
                        note={`${summary.emailsInRange} email · ${summary.smsInRange} SMS · ${summary.messagesTotal} all time`}
                    />
                    <StatTile
                        label="Due today"
                        value={summary.dueToday}
                        note={`${summary.capped} reached the 5-chase cap · ${summary.stopped ?? 0} stopped · ${summary.completed} completed · ${summary.awaitingLink} waiting for e-sign link`}
                    />
                </div>
            )}

            <p className={localStyles.sectionNote}>
                A client enters the chase when their claim form is submitted but the CFA is not signed, or it is signed
                without both identity documents. Dashboard-onboarded clients only join once an agent has sent the e-sign
                link; rejected leads are never chased. The first chase goes out after 1 day, then every 2 days, up to 5
                messages, by email and SMS where we hold them. Use <strong>Stop</strong> on a row (or on the client
                record) if a client opts out — the daily chase will skip them until resumed.
            </p>

            <h3 className={localStyles.sectionTitle}>Enrolled clients</h3>
            <div className={styles.toolbar}>
                <div className={styles.searchWrap}>
                    <FaSearch className={styles.searchIcon} />
                    <input
                        className={styles.searchInput}
                        placeholder="Search name, email, phone or case ref"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        aria-label="Search enrolled clients"
                    />
                </div>
                <select className={styles.select} value={filter} onChange={(e) => setFilter(e.target.value as EnrolFilter)} aria-label="Enrolment filter">
                    <option value="active">Being chased</option>
                    <option value="due">Due today</option>
                    <option value="stopped">Stopped (opted out)</option>
                    <option value="capped">Reached cap (5)</option>
                    <option value="awaiting_link">Not yet chaseable</option>
                    <option value="complete">Completed journey</option>
                    <option value="all">All campaigns</option>
                </select>
                <span className={styles.resultCount}>{enrolled.length} clients</span>
            </div>

            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Client</th>
                            <th>Source</th>
                            <th>Submitted</th>
                            <th>Status</th>
                            <th className="num">Chases</th>
                            <th>Last chase</th>
                            <th>Next due</th>
                            <th aria-label="Actions" />
                        </tr>
                    </thead>
                    <tbody>
                        {loading && !data ? (
                            <tr>
                                <td colSpan={8} className={styles.emptyState}>
                                    Loading…
                                </td>
                            </tr>
                        ) : rows.length === 0 ? (
                            <tr>
                                <td colSpan={8} className={styles.emptyState}>
                                    No clients match this filter.
                                </td>
                            </tr>
                        ) : (
                            rows.map((e) => {
                                const due = e.next_chase_due
                                const overdue = !!due && due <= todayIso()
                                const stopped = e.status === 'stopped'
                                const busy = updatingRef === e.case_ref
                                return (
                                    <tr key={e.esign_id} className={styles.rowClickable} onClick={() => navigate(`/military-hearing-loss/client/${e.client_id}`)}>
                                        <td>
                                            <div className={styles.stackCell}>
                                                <span className={styles.stackPrimary}>
                                                    {e.firstname} {e.lastname}
                                                </span>
                                                <span className={styles.stackSecondary}>
                                                    {e.email || '—'}
                                                    {e.telephone ? ` · ${e.telephone}` : ''}
                                                </span>
                                                <span className={`${styles.stackSecondary} ${localStyles.mono}`}>{e.case_ref}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <Pill tone={e.import_cohort ? 'muted' : e.submitted_via === 'dashboard' ? 'info' : 'brand'}>
                                                {e.import_cohort ? 'Imported' : e.submitted_via === 'dashboard' ? 'Dashboard' : 'Website'}
                                            </Pill>
                                        </td>
                                        <td>
                                            <span className={styles.stackSecondary}>{formatDateTime(e.submitted_at)}</span>
                                        </td>
                                        <td>
                                            <div className={styles.stackCell}>
                                                <Pill tone={chaseTone(e.status)} dot>
                                                    {CHASE_LABELS[e.status]}
                                                </Pill>
                                                <span className={styles.stackSecondary}>
                                                    {e.signed ? `Signed ${formatDate(e.signature_date)}` : e.link_opened_at ? 'Link opened' : e.link_sent_at ? 'Link sent' : 'Link not opened'}
                                                    {` · ID docs ${e.identity_docs_uploaded}/2`}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="num">
                                            <span className={styles.stackSecondary}>
                                                {e.num_chases}/5{e.capped ? ' · capped' : ''}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={styles.stackSecondary}>{formatDateTime(e.last_chase_date)}</span>
                                        </td>
                                        <td>
                                            {e.status === 'complete' ? (
                                                <Pill tone="good">Done</Pill>
                                            ) : stopped ? (
                                                <div className={styles.stackCell}>
                                                    <Pill tone="bad">Stopped</Pill>
                                                    <span className={styles.stackSecondary}>
                                                        {formatDate(e.chase_stopped_at)}
                                                        {e.chase_stopped_by ? ` · ${e.chase_stopped_by}` : ''}
                                                        {e.chase_stopped_reason ? ` · ${e.chase_stopped_reason}` : ''}
                                                    </span>
                                                </div>
                                            ) : e.status === 'awaiting_link' || e.status === 'rejected' ? (
                                                <Pill tone="muted">Not chased</Pill>
                                            ) : e.capped ? (
                                                <Pill tone="muted">Cap reached</Pill>
                                            ) : due ? (
                                                <Pill tone={overdue ? 'warn' : 'info'}>{overdue ? `Due ${formatDate(due)}` : formatDate(due)}</Pill>
                                            ) : (
                                                <span className={styles.stackSecondary}>—</span>
                                            )}
                                        </td>
                                        <td className={localStyles.rowActionCell} onClick={(ev) => ev.stopPropagation()}>
                                            {e.active ? (
                                                <button
                                                    type="button"
                                                    className={`${styles.btn} ${styles.btnDangerGhost} ${localStyles.rowActionBtn}`}
                                                    onClick={() => setChaseStopped(e, true)}
                                                    disabled={busy}
                                                    title="Stop chase messages for this client"
                                                >
                                                    <FaBan size={10} /> {busy ? '…' : 'Stop'}
                                                </button>
                                            ) : stopped ? (
                                                <button
                                                    type="button"
                                                    className={`${styles.btn} ${localStyles.rowActionBtn}`}
                                                    onClick={() => setChaseStopped(e, false)}
                                                    disabled={busy}
                                                    title="Resume chase messages for this client"
                                                >
                                                    <FaPlay size={9} /> {busy ? '…' : 'Resume'}
                                                </button>
                                            ) : null}
                                        </td>
                                    </tr>
                                )
                            })
                        )}
                    </tbody>
                </table>
                <TablePagination page={page} totalPages={totalPages} total={enrolled.length} pageSize={pageSize} onChange={setPage} />
            </div>

            <h3 className={localStyles.sectionTitle}>Messages sent ({rangeLabel})</h3>
            {data?.messagesError && (
                <div className={styles.errorBanner}>Could not load sent messages: {data.messagesError}</div>
            )}
            {data && data.messagesByDay.length > 0 && (
                <div className={styles.tableWrap} style={{ marginBottom: 14 }}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Day</th>
                                <th>Type</th>
                                <th>Message</th>
                                <th className="num">Sent</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.messagesByDay.map((row) => (
                                <tr key={`${row.day}-${row.com_type}`}>
                                    <td>
                                        <span className={styles.stackSecondary}>{formatDate(row.day)}</span>
                                    </td>
                                    <td>
                                        <Pill tone={row.com_type === 'sms' ? 'info' : 'brand'}>{row.com_type === 'sms' ? 'SMS' : 'Email'}</Pill>
                                    </td>
                                    <td>
                                        <span className={styles.stackSecondary}>{row.title || '—'}</span>
                                    </td>
                                    <td className="num">
                                        <span className={styles.stackPrimary} style={{ fontSize: 13 }}>
                                            {row.count}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <ChaseMessagesTable
                messages={data?.messages || []}
                loading={loading && !data}
                clientNames={clientNames}
                emptyMessage={`No chase messages were sent in the ${rangeLabel}.`}
            />
        </>
    )
}
